import { useEffect, useRef, useState } from 'react';

/**
 * Hook to measure the width of text dynamically using canvas measurement.
 *
 * This hook creates a temporary canvas element to accurately measure text width
 * based on the specified font properties. Useful for calculating dynamic padding
 * or spacing based on text content.
 *
 * @param text - The text to measure
 * @param fontSize - Font size in pixels (default: 14 for text-sm)
 * @param fontFamily - Font family (default: system fonts used by Tailwind)
 * @returns Object containing the measured width in pixels and calculated padding in rem
 */
export function useTextWidth(
  text: string = '',
  fontSize: number = 14, // text-sm in Tailwind
  fontFamily: string = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
) {
  const [textWidth, setTextWidth] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!text) {
      setTextWidth(0);
      return;
    }

    // Create or reuse canvas for text measurement
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      setTextWidth(0);
      return;
    }

    // Set font properties to match the actual rendered text
    context.font = `${fontSize}px ${fontFamily}`;

    // Measure the text width
    const metrics = context.measureText(text);
    const width = metrics.width;

    setTextWidth(width);
  }, [text, fontSize, fontFamily]);

  // Calculate padding in rem units (16px = 1rem)
  // Add extra padding for spacing (8px on each side = 16px total)
  const paddingInRem = (textWidth + 16) / 16;

  // Round up to nearest 0.25rem for consistent spacing
  const roundedPadding = Math.ceil(paddingInRem * 4) / 4;

  return {
    /** Width of the text in pixels */
    width: textWidth,
    /** Calculated padding-right value in rem units */
    paddingRem: roundedPadding,
    /** Calculated padding-right as CSS custom property */
    paddingStyle: { paddingRight: `${roundedPadding}rem` },
  };
}
